import React, { useEffect, useState } from 'react';
import { Button, Field, Header, Input, Message, Modal, Toast } from 'react-frontier';
import { SetLoading, UserClass } from '../SiteClasses';
import { bindFormChange } from '../SiteUtil';
import moment from 'moment';
import API from '../API';
import Validator from '../Validator';
import SearchUserModal from './SearchUserModal';
import SearchEmploymentModal, { EmploymentSearchItem } from './SearchEmploymentModal';

interface AdjustmentEmploymentResolution{
	selected_employment_id?: number,
	require_selection: boolean,
	options: EmploymentSearchItem[],
}

interface EmploymentAdjustmentForm{
	date: number,
	amount: string,
	notes: string,
	employment_id?: number,
}

interface EmploymentAdjustmentModalProps{
	open: boolean,
	onClose: ()=>void,
	weekStart: number,
	weekEnd: number,
	onCreated: (payment: any, logs: any[])=>void,
}

var EmploymentAdjustmentModal = (props: EmploymentAdjustmentModalProps)=>{
	var [selectedUser, setSelectedUser] = useState<UserClass>(null);
	var [showUserSearch, setShowUserSearch] = useState(false);
	var [showEmploymentSearch, setShowEmploymentSearch] = useState(false);
	var [form, setForm] = useState<EmploymentAdjustmentForm>({
		date: moment().startOf('day').unix(),
		amount: '',
		notes: '',
		employment_id: null,
	});
	var [prompts, setPrompts] = useState<string[]>([]);
	var [employmentResolution, setEmploymentResolution] = useState<AdjustmentEmploymentResolution>(null);
	var [selectedEmployment, setSelectedEmployment] = useState<EmploymentSearchItem>(null);
	var [loadingEmployments, setLoadingEmployments] = useState(false);
	var selectedUserName = selectedUser?.name || [selectedUser?.first_name, selectedUser?.last_name].filter(Boolean).join(' ');

	useEffect(()=>{
		if(!props.open) return;
		setSelectedUser(null);
		setShowUserSearch(false);
		setShowEmploymentSearch(false);
		setEmploymentResolution(null);
		setSelectedEmployment(null);
		setPrompts([]);
		setForm({
			date: moment().startOf('day').unix(),
			amount: '',
			notes: '',
			employment_id: null,
		});
	}, [props.open, props.weekStart]);

	var loadEmploymentOptions = (user_id: number)=>{
		setLoadingEmployments(true);
		setEmploymentResolution(null);
		setSelectedEmployment(null);
		setForm(prev => ({ ...prev, employment_id: null }));

		API.getEmploymentAdjustmentOptions(user_id).then(res=>{
			if(res.error){
				setPrompts([res.message]);
				return;
			}
			setEmploymentResolution(res.data);
			var defaultEmployment = (res.data.options || []).find((option: EmploymentSearchItem)=> option.employment_id === res.data.selected_employment_id) || null;
			setSelectedEmployment(defaultEmployment);
			setForm(prev => ({
				...prev,
				employment_id: res.data.selected_employment_id || null,
			}));
		}).catch(()=>{
			setPrompts(['Hubo un error resolviendo la taquilla del ajuste (LCL-1)']);
		}).finally(()=>{
			setLoadingEmployments(false);
		});
	};

	var maxDate = moment().startOf('day').unix();
	var onFormChange = bindFormChange(form, setForm);
	var onDateChange = (date: number)=>{
		if(!date){
			setForm(prev => ({ ...prev, date: null }));
			return;
		}
		var nextDate = moment.unix(date).startOf('day');
		var minDate = moment.unix(props.weekStart).startOf('day');
		if(nextDate.isBefore(minDate, 'day')) nextDate = minDate;
		if(nextDate.isAfter(moment.unix(maxDate), 'day')) nextDate = moment.unix(maxDate);
		setForm(prev => ({ ...prev, date: nextDate.unix() }));
	};
	var selectedDate = form.date ? moment.unix(form.date).startOf('day').unix() : null;
	var selectedDateLabel = form.date ? moment.unix(form.date).format('DD/MM/YYYY') : '';
	var weekLabel = `${moment.unix(props.weekStart).format('DD/MM/YYYY')} - ${moment.unix(props.weekEnd).format('DD/MM/YYYY')}`;
	var employmentOptions = employmentResolution?.options || [];
	var isDateAllowed = (date: number)=>{
		if(!date) return false;
		var dayStart = moment.unix(date).startOf('day').unix();
		return dayStart >= props.weekStart && dayStart <= maxDate;
	};
	var payableLabel = `Entra en el corte pendiente (${weekLabel})`;

	var createAdjustment = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({
			user_id: selectedUser?.user_id,
			date: form.date,
			amount: form.amount,
			employment_id: form.employment_id,
		}, {
			user_id: [{ rule: 'number', prompt: 'Selecciona un usuario.' }],
			date: [
				{ rule: 'number', prompt: 'Selecciona una fecha válida.' },
				{
					skipEmpty: true,
					rule: value => isDateAllowed(value),
					prompt: 'La fecha del ajuste no puede ser futura ni anterior al corte pendiente.',
				},
			],
			amount: [{
				rule: value => !Number.isNaN(parseFloat((value || '').toString())),
				prompt: 'Ingresa un monto válido.',
			}, {
				rule: value => Math.round(parseFloat((value || '').toString()) * 100) !== 0,
				prompt: 'El monto del ajuste no puede ser $0.',
			}],
			employment_id: [{
				if: !!selectedUser && !loadingEmployments,
				rule: 'number',
				prompt: 'Selecciona una taquilla para registrar el ajuste.',
			}],
		});
		setPrompts(prompts);
		if(!valid) return;
		if(loadingEmployments) return setPrompts(['Espera a que se resuelva la taquilla del ajuste.']);

		var amount = Math.round(parseFloat((form.amount || '').toString()) * 100) / 100;

		setLoading(true);
		API.createEmploymentAdjustment(
			selectedUser.user_id,
			selectedDate,
			amount,
			form.notes,
			form.employment_id || undefined
		).then(res=>{
			if(res.error){
				setPrompts([res.message]);
				return;
			}
			Toast.success(`Se registró el ajuste para ${selectedUserName} el ${selectedDateLabel}.`);
			props.onCreated(res.data.payment, res.data.logs || []);
			props.onClose();
		}).catch(()=>{
			setPrompts(['Hubo un error registrando el ajuste (LCL-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	return <>
		<Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>Registrar ajuste</Modal.Header>
			<Modal.Content>
				<Message type='info' text={payableLabel} style={{ marginBottom: 15, textAlign: 'center' }} />
				<Field label='Usuario'>
					<Input
						value={selectedUserName}
						placeholder='Selecciona un usuario...'
						readonly
						button={<Button text='Seleccionar' onClick={()=> setShowUserSearch(true)} />}
					/>
				</Field>
				<Field>
					<Input
						label='Fecha'
						value={form.date}
						onChange={onDateChange}
						calendar={{
							date: form.date || props.weekStart,
							mode: 'date',
							format: 'DD/MM/YYYY',
							minDate: props.weekStart,
							maxDate,
						}}
						style={{ width: '100%' }}
					/>
				</Field>
				<Field>
					<Input
						label='Monto'
						value={form.amount}
						onChange={onFormChange('amount')}
						inputType='number'
						placeholder='0.00'
						style={{ width: '100%' }}
					/>
				</Field>
				{selectedUser ? (
					loadingEmployments ? (
						<div style={{ marginTop: 10, marginBottom: 10 }}>
							<Header loading text='Resolviendo taquilla...' size='small' />
						</div>
					) : (
						<Field label='Taquilla'>
							<Input
								value={selectedEmployment?.employment_name || ''}
								placeholder='Selecciona una taquilla...'
								readonly
								button={<Button text='Seleccionar' onClick={()=> setShowEmploymentSearch(true)} />}
							/>
						</Field>
					)
				) : null}
				<Field label='Comentarios'>
					<Input
						value={form.notes}
						onChange={onFormChange('notes')}
						placeholder='Opcional'
						textarea
					/>
				</Field>
				<Message type='error' list={prompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={props.onClose} />
				<Button text='Guardar' color='black' onClick={createAdjustment} />
			</Modal.Actions>
		</Modal>
		<SearchUserModal
			open={showUserSearch}
			onClose={()=> setShowUserSearch(false)}
			title='Seleccionar usuario'
			pageSize={20}
			onSelected={(user: UserClass)=>{
				setSelectedUser(user);
				setPrompts([]);
				setShowUserSearch(false);
				loadEmploymentOptions(user.user_id);
			}}
		/>
		<SearchEmploymentModal
			open={showEmploymentSearch}
			onClose={()=> setShowEmploymentSearch(false)}
			onlyActive
			employments={employmentOptions}
			onSelected={(employment)=>{
				setSelectedEmployment(employment);
				setForm(prev => ({ ...prev, employment_id: employment.employment_id }));
				setShowEmploymentSearch(false);
			}}
		/>
	</>;
}

export default EmploymentAdjustmentModal;
